// client/src/pages/pmPortal/PmPortalChangePasswordPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePmPortalAuth } from '../../hooks/usePmPortalAuth';

const MIN_LENGTH = 10;

const PmPortalChangePasswordPage = () => {
    const { role, logout } = usePmPortalAuth();
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < MIN_LENGTH) {
            return setError(`Please choose at least ${MIN_LENGTH} characters.`);
        }
        if (password !== confirm) return setError('The two passwords do not match.');

        setSaving(true);
        try {
            const response = await fetch('/api/pm-portal/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('pmPortalToken')}`,
                },
                body: JSON.stringify({ new_password: password }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Could not change the password.');
            // A password change signs out every other session; this one continues on a fresh token.
            if (data.token) localStorage.setItem('pmPortalToken', data.token);
            navigate(role === 'owner' ? '/pm-portal/owner' : '/pm-portal/tenant');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white p-8 rounded-lg shadow">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Choose a password</h1>
                <p className="text-sm text-slate-600 mb-6">
                    The office issued you a temporary password. Replace it before continuing, so
                    nobody else holds a working password to your records.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={`New password (at least ${MIN_LENGTH} characters)`}
                        required
                        autoComplete="new-password"
                        className="w-full p-3 border rounded-md"
                    />
                    <input
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Repeat the new password"
                        required
                        autoComplete="new-password"
                        className="w-full p-3 border rounded-md"
                    />
                    {error && <p className="text-red-600 text-sm">{error}</p>}
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full px-4 py-3 text-white bg-slate-800 rounded-md hover:bg-slate-900 disabled:bg-indigo-400"
                    >
                        {saving ? 'Saving…' : 'Save and Continue'}
                    </button>
                </form>
                <button onClick={logout} className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700">
                    Sign out instead
                </button>
            </div>
        </div>
    );
};

export default PmPortalChangePasswordPage;
